// src/components/AccountMenu.jsx
//
// The topbar avatar menu, shared by all four portals.
//
// Why one component: navigator/doctor and subscriber each carried their own
// near-identical copy of this dropdown, and the patient portal had no dropdown
// at all — its avatar called logout() on a single click, so a mis-aimed tap
// signed the patient straight out with no confirmation and no way back except
// logging in again. That is the accidental-logout hazard the navigator copy's
// own comment says the dropdown exists to prevent; the patient portal, the one
// used on phones where mis-taps are likeliest, was the portal missing it.
//
// Every portal now gets: avatar button → dropdown showing who you are signed
// in as, an optional extra block (the patient portal puts its plan there),
// an optional Settings link, then a clearly labelled Sign out.

import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import Icon from './Icon';

/**
 * @param {string}  initials     1–2 letters for the avatar (fallback when no avatarUrl)
 * @param {string}  [avatarUrl]  a photo to show instead of initials — an object
 *        URL from a blob fetch, not a bare API path (the avatar endpoints are
 *        authenticated; only the patient portal supplies this today, see
 *        PatientContext's own avatarUrl)
 * @param {string}  name         display name in the dropdown header
 * @param {string}  role         role line under the name ("Patient", "Doctor"…)
 * @param {string}  [secondary]  third line — email or phone
 * @param {string}  [settingsTo] route for the Settings link; omitted = no link
 * @param {node}    [children]   extra block rendered under the identity header
 * @param {func}    onLogout     called when Sign out is chosen
 */
export default function AccountMenu({
  initials,
  avatarUrl,
  name,
  role,
  secondary,
  settingsTo,
  children,
  onLogout,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape — a dropdown with no keyboard exit is unusable with a
  // screen reader.
  useEffect(() => {
    if (!open) return undefined;
    function handleKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleLogout = () => {
    setOpen(false);
    onLogout?.();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="topbar-avatar"
        onClick={() => setOpen(o => !o)}
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="true"
        title={name ? `${name}${role ? ` · ${role}` : ''}` : 'Account menu'}
      >
        {avatarUrl
          ? <img src={avatarUrl} alt="" className="topbar-avatar-img" />
          : initials}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 10, minWidth: 220,
            // Caps the width on small screens so the menu can't run off the
            // edge of a phone — the topbar avatar is visible on mobile too.
            maxWidth: 'calc(100vw - 24px)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 200, overflow: 'hidden',
          }}
        >
          {/* Who you are signed in as */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--bg-canvas)',
            background: 'var(--bg-subtle)',
          }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
              {name}
            </div>
            {role && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                {role}
              </div>
            )}
            {secondary && (
              <div style={{
                fontSize: 11, color: 'var(--text-muted)', marginTop: 1,
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {secondary}
              </div>
            )}
          </div>

          {/* Portal-specific block (the patient portal puts its plan here) */}
          {children}

          {settingsTo && (
            <NavLink
              to={settingsTo}
              onClick={() => setOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 16px', fontSize: 13, color: 'var(--text-primary)',
                textDecoration: 'none', transition: 'background .12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon name="settings" size={16} /> Settings
            </NavLink>
          )}

          <div style={{ height: 1, background: 'var(--bg-canvas)' }} />

          <button
            role="menuitem"
            onClick={handleLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '11px 16px', fontSize: 13,
              color: 'var(--red)', background: 'none', border: 'none',
              cursor: 'pointer', fontFamily: 'var(--font-body)',
              textAlign: 'left', transition: 'background .12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--red-bg)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Icon name="logout" size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
